import React, { useState, useEffect, useCallback } from 'react';
import useVideoStore from './stores/useVideoStore';
import useVideoAutosave from './hooks/useVideoAutosave';
import useCsrfToken from '../ArticleFormNew/hooks/useCsrfToken';
import { TranslationProvider, useTranslations } from '../ArticleFormNew/contexts/TranslationContext';
import { completeOnboarding } from './api';
import AutosaveIndicator from '../shared/AutosaveIndicator';
import VideoTitle from './VideoTitle';
import FormBody from './FormBody';
import FormSidebar from './FormSidebar';
import VideoPublishPanel from './VideoPublishPanel';
import VideoOnboardingTour from './VideoOnboardingTour';
import VideoProgressBar from './VideoProgressBar';

function VideoFormInner({ mode, onboardingCompleted, hospitals, video }) {
  var t = useTranslations();
  var csrfToken = useCsrfToken();
  var setVideo = useVideoStore(function(state) { return state.setVideo; });
  var resetStore = useVideoStore(function(state) { return state.resetStore; });
  var publishErrors = useVideoStore(function(state) { return state.publishErrors; });
  var saveStatus = useVideoStore(function(state) { return state.saveStatus; });
  var lastSavedAt = useVideoStore(function(state) { return state.lastSavedAt; });
  var autosave = useVideoAutosave();

  var onboardingState = useState(!onboardingCompleted);
  var showOnboarding = onboardingState[0];
  var setShowOnboarding = onboardingState[1];

  useEffect(function() {
    resetStore();
    if (mode === 'edit' && video) {
      setVideo(video);
    }
    setShowOnboarding(!onboardingCompleted);
  }, []);

  var handleOnboardingComplete = useCallback(function() {
    setShowOnboarding(false);
    completeOnboarding(csrfToken).catch(function() {});
  }, [csrfToken]);

  var pageTitle = mode === 'edit' ? t.edit_title : t.create_title;

  return (
    <div className="article-form-new">
      <div className="card mb-3 article-form-new__header-card">
        <div className="card-body">
          <div className="article-form-new__header">
            <div className="article-form-new__header-left">
              <a className="article-form-new__back" href="/articles/videos" data-turbolinks="false">
                <i className="zmdi zmdi-arrow-left" />
              </a>
              <div className="article-form-new__title-row">
                <h4 className="article-form-new__title">{pageTitle}</h4>
                <span data-tour="autosave">
                  <AutosaveIndicator saveStatus={saveStatus} lastSavedAt={lastSavedAt} onRetry={autosave.saveNow} translations={t} />
                </span>
              </div>
              {publishErrors && Object.keys(publishErrors).length > 0 && (
                <div className="article-form-new__header-errors">
                  {Object.entries(publishErrors).map(function(entry) {
                    var field = entry[0];
                    var messages = entry[1];
                    return messages.map(function(msg, i) {
                      return (
                        <span key={field + '-' + i} className="article-form-new__header-error">
                          <i className="zmdi zmdi-alert-circle-o" />
                          {field !== 'base' ? field.replace('_', ' ') + ': ' : ''}{msg}
                        </span>
                      );
                    });
                  })}
                </div>
              )}
            </div>
            <div className="article-form-new__header-right" data-tour="publish_panel">
              <VideoPublishPanel saveNow={autosave.saveNow} compact />
              <button
                type="button"
                className="article-form-new__tour-btn"
                onClick={function() { setShowOnboarding(true); }}
                title={t.guided_tour}
              >
                <i className="zmdi zmdi-help-outline" />
              </button>
            </div>
          </div>
          <VideoProgressBar />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card">
            <div className="card-body">
              <VideoTitle scheduleAutosave={autosave.scheduleAutosave} />
              <FormBody
                scheduleAutosave={autosave.scheduleAutosave}
                saveNow={autosave.saveNow}
                csrfToken={csrfToken}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 article-form-new__sidebar-col">
          <div className="form-sidebar">
            <FormSidebar
              hospitals={hospitals}
              scheduleAutosave={autosave.scheduleAutosave}
            />
          </div>
        </div>
      </div>

      {showOnboarding && (
        <VideoOnboardingTour
          isOpen={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
    </div>
  );
}

function VideoFormNew(props) {
  return (
    <TranslationProvider translations={props.translations}>
      <VideoFormInner
        mode={props.mode}
        onboardingCompleted={props.onboardingCompleted}
        hospitals={props.hospitals}
        video={props.video}
      />
    </TranslationProvider>
  );
}

export default VideoFormNew;
